import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { User, Settings, LogOut, Shield } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';

import { logger } from '@/utils/logger';
export const MobileUserProfile: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const getDisplayName = () => {
    return user?.first_name || user?.username || user?.full_name || user?.email || 'User';
  };

  const getRoleLabel = () => {
    return user?.roles?.[0] || 'User';
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: "Signed out successfully",
        description: "You have been signed out of your account.",
      });
      navigate('/');
    } catch (error) {
      logger.error('Sign out error:', error);
      toast({
        title: "Sign out failed",
        description: "There was an error signing you out. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleProfileSettings = () => {
    toast({
      title: "Feature Coming Soon",
      description: "Profile settings will be available in a future update.",
    });
  };

  const handleAccountPreferences = () => {
    toast({
      title: "Feature Coming Soon", 
      description: "Account preferences will be available in a future update.",
    });
  };

  return (
    <div className="p-4 space-y-4">
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-foreground mb-2">Settings</h1>
        <p className="text-muted-foreground">Manage your profile and account preferences</p>
      </div>

      {/* User Info Card */}
      <Card className="bg-card/30 border-border">
        <CardHeader>
          <CardTitle className="text-foreground text-lg flex items-center">
            <User className="h-5 w-5 mr-2 text-primary" />
            User Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div>
            <p className="text-sm text-muted-foreground">Name</p>
            <p className="text-foreground font-medium">{getDisplayName()}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Email</p>
            <p className="text-foreground font-medium">{user?.email || 'Not available'}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Role</p>
            <p className="text-foreground font-medium flex items-center">
              <Shield className="h-4 w-4 mr-1 text-primary" />
              {getRoleLabel()}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Settings Options */}
      <div className="grid grid-cols-1 gap-4">
        <Card className="bg-card/50 border-border hover:bg-card/70 transition-colors cursor-pointer" onClick={handleProfileSettings}>
          <CardContent className="p-4 flex items-center">
            <User className="h-8 w-8 mr-4 text-primary" />
            <div>
              <p className="text-foreground font-medium">Profile Settings</p>
              <p className="text-sm text-muted-foreground">Update your personal information</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card/50 border-border hover:bg-card/70 transition-colors cursor-pointer" onClick={handleAccountPreferences}>
          <CardContent className="p-4 flex items-center">
            <Settings className="h-8 w-8 mr-4 text-primary" />
            <div>
              <p className="text-foreground font-medium">Account Preferences</p>
              <p className="text-sm text-muted-foreground">Manage your account settings</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card/50 border-border hover:bg-card/70 transition-colors cursor-pointer" onClick={handleSignOut}>
          <CardContent className="p-4 flex items-center">
            <LogOut className="h-8 w-8 mr-4 text-destructive" />
            <div>
              <p className="text-foreground font-medium">Sign Out</p>
              <p className="text-sm text-muted-foreground">Sign out of your account</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};